import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { endOfDay, startOfDay } from 'date-fns';
import { forkJoin, map, Observable } from 'rxjs';
import {
    IBlenderAgent,
    IAssignToMe,
    ICompleteWorkItemParams,
    IInternalHistoricalWorkItems,
    IInternalHistoricalWorkItem,
    IAgentRelations,
    IWorkItemRelation,
    IOperatorIdResponse,
} from '@dxp/core/models';
import { ChannelType, Direction, LOGGER_REQUEST_NAME } from '@dxp/core/types';
import { environment } from '@dxp/core/environment';
import { toIsoStringIgnoreTimezone } from '@dxp/core/helpers';

@Injectable({
    providedIn: 'root',
})
export class AgentApiService {
    readonly #http = inject(HttpClient);

    getActiveAgents(
        orderBy: string,
        filters: {
            requiresName?: boolean;
            requiresEmail?: boolean;
            requiresPhone?: boolean;
        },
        searchQuery?: string,
        businessUnitId?: string | null,
    ): Observable<IBlenderAgent[]> {
        const query =
            businessUnitId === undefined
                ? 'allAgents=true'
                : `businessUnitId=${businessUnitId ?? ''}`;

        return this.#http
            .get<IBlenderAgent[]>(`${environment.blenderUrl}/agents?${query}`, {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Agent Service (Get Active Agents)',
                ),
            })
            .pipe(
                map(agents => {
                    const filteredResults = agents.filter(agent => {
                        if (
                            searchQuery &&
                            !agent.name
                                .toLowerCase()
                                .includes(searchQuery.toLowerCase())
                        ) {
                            return false;
                        }

                        if (
                            filters.requiresName &&
                            (!agent.name || agent.name.trim() === '')
                        ) {
                            return false;
                        }
                        if (
                            filters.requiresEmail &&
                            (!agent.email || agent.email.trim() === '')
                        ) {
                            return false;
                        }
                        if (
                            filters.requiresPhone &&
                            (!agent.phone || agent.phone.trim() === '')
                        ) {
                            return false;
                        }
                        return true;
                    });

                    const [property, direction] = orderBy.split(' ');

                    const sortKey = property as keyof Pick<
                        IBlenderAgent,
                        'name' | 'phone' | 'email'
                    >;
                    const ascending = direction?.toLowerCase() !== 'desc';

                    filteredResults.sort((a, b) => {
                        const aValue = (a[sortKey] ?? '').toLowerCase();
                        const bValue = (b[sortKey] ?? '').toLowerCase();

                        if (aValue < bValue) return ascending ? -1 : 1;
                        if (aValue > bValue) return ascending ? 1 : -1;
                        return 0;
                    });

                    return filteredResults;
                }),
            );
    }

    reserveForLookup() {
        this.#http
            .post<string>(
                environment.blenderUrl + '/resource/reserveForLookup',
                null,
                {
                    context: new HttpContext().set(
                        LOGGER_REQUEST_NAME,
                        'Agent Service (Look up activated)',
                    ),
                },
            )
            .subscribe();
    }

    assignToMe(conversationId: string): Observable<IAssignToMe> {
        return this.#http.post<IAssignToMe>(
            environment.blenderUrl + `/resource/${conversationId}/assignToMe`,
            null,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Agent Service (Conversation assigned to Agent)',
                ),
            },
        );
    }

    resetWrapup(workitemId: string) {
        this.#http
            .post<void>(
                environment.blenderUrl +
                    `/resource/resetwrapup?workitemId=${workitemId}`,
                null,
                {
                    context: new HttpContext().set(
                        LOGGER_REQUEST_NAME,
                        'Agent Service (Reset wrapup)',
                    ),
                },
            )
            .subscribe();
    }

    completeWorkItem(params: ICompleteWorkItemParams) {
        this.#http
            .post<void>(
                environment.blenderUrl + '/resource/completeWorkItem',
                params,
                {
                    context: new HttpContext().set(
                        LOGGER_REQUEST_NAME,
                        'Agent Service (Work item completed)',
                    ),
                },
            )
            .subscribe();
    }

    getWorkitemHistoryByAgent(
        agentId: string,
        count: boolean,
        expand: string[],
        skip: number,
        top: number,
        filters: {
            queues?: string[];
            channels?: number[];
            channelTypes?: ChannelType[];
            direction?: Direction;
            completionCodes?: string[];
            date?: { from: Date | null; to: Date | null };
        },
        orderBy: string,
    ): Observable<IInternalHistoricalWorkItems> {
        const agentFilter = `(AgentId eq '${agentId}')`;
        const channelFilter = filters.channels?.length
            ? `(primaryChannelId in (${filters.channels.join(',')}))`
            : undefined;
        const channelTypesFilter = filters.channelTypes?.length
            ? `(channel/channelType in (${filters.channelTypes.map(id => `'${id}'`).join(',')}))`
            : undefined;
        const completionCodesFilter = filters.completionCodes?.length
            ? `(completionCode in (${filters.completionCodes.map(id => `'${id}'`).join(',')}))`
            : undefined;
        const queueFilter = filters.queues?.length
            ? `(queueId in (${filters.queues.map(id => `'${id}'`).join(',')}))`
            : undefined;

        let dateFilter = undefined;

        if (filters.date?.from && filters.date?.to) {
            const from = toIsoStringIgnoreTimezone(
                startOfDay(filters.date.from),
            );
            const to = toIsoStringIgnoreTimezone(endOfDay(filters.date.to));

            dateFilter = `(startTimeUtc gt ${from} and startTimeUtc lt ${to})`;
        }

        const filter = [
            agentFilter,
            queueFilter,
            channelFilter,
            channelTypesFilter,
            filters.direction === 'OutBound'
                ? ' (primaryConversation/isOutbound eq true)'
                : undefined,
            filters.direction === 'InBound'
                ? ' (primaryConversation/isOutbound eq false)'
                : undefined,
            completionCodesFilter,
            dateFilter,
        ]
            .filter(x => !!x)
            .join(' and ');

        const params = new HttpParams()
            .set('$count', count.toString())
            .set('$expand', expand.join(','))
            .set('$skip', skip.toString())
            .set('$top', top.toString())
            .set('$orderBy', orderBy)
            .set('$filter', filter);

        return this.#http
            .get<{
                '@odata.count': number;
                value: IInternalHistoricalWorkItem[];
            }>(`${environment.odataBaseUrl}/v1/workItems`, {
                params,
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Agent Service (Get Workitem History By Agent)',
                ),
            })
            .pipe(
                map(response => {
                    return {
                        count: response['@odata.count'],
                        historicalWorkitems: response.value,
                    };
                }),
            );
    }

    getRelations(
        agentId: string,
        channelTypes: ChannelType[],
        from: Date,
        to: Date,
    ): Observable<IAgentRelations> {
        const mediaGroups =
            channelTypes.length === 0 ? ['voice', 'chat', 'email'] : [];

        if (
            channelTypes.some(type =>
                ['webchat', 'whatsapp', 'instagram', 'facebook'].includes(type),
            )
        ) {
            mediaGroups.push('chat');
        }
        if (channelTypes.includes('voice')) {
            mediaGroups.push('voice');
        }
        if (channelTypes.includes('email')) {
            mediaGroups.push('email');
        }

        const uniqueMediaGroups = [...new Set(mediaGroups)];

        const requests = uniqueMediaGroups.map(mediaGroup => {
            const payload = {
                agentIds: [agentId],
                mediaGroup: mediaGroup,
                from,
                to,
            };

            return forkJoin([
                this.#http.post<IWorkItemRelation[]>(
                    `${environment.odataBaseUrl}/extras/workitems/relations/channel`,
                    payload,
                    {
                        context: new HttpContext().set(
                            LOGGER_REQUEST_NAME,
                            'Agent Service (WorkItems Channel)',
                        ),
                    },
                ),
                this.#http.post<IWorkItemRelation[]>(
                    `${environment.odataBaseUrl}/extras/workitems/relations/queue`,
                    payload,
                    {
                        context: new HttpContext().set(
                            LOGGER_REQUEST_NAME,
                            'Agent Service (WorkItems Queue)',
                        ),
                    },
                ),
                this.#http.post<IWorkItemRelation[]>(
                    `${environment.odataBaseUrl}/extras/workitems/relations/completioncode`,
                    payload,
                    {
                        context: new HttpContext().set(
                            LOGGER_REQUEST_NAME,
                            'Agent Service (WorkItems Completion Code)',
                        ),
                    },
                ),
            ]);
        });

        return forkJoin(requests).pipe(
            map(results => {
                const merged: IAgentRelations = {
                    channels: [],
                    queues: [],
                    completionCodes: [],
                };

                for (const [channels, queues, completionCodes] of results) {
                    merged.channels.push(...channels);
                    merged.queues.push(...queues);
                    merged.completionCodes.push(...completionCodes);
                }

                const dedupe = (items: IWorkItemRelation[]) =>
                    Array.from(
                        new Map(items.map(item => [item.id, item])).values(),
                    );

                return {
                    channels: dedupe(merged.channels),
                    queues: dedupe(merged.queues),
                    completionCodes: dedupe(merged.completionCodes),
                };
            }),
        );
    }

    getOperatorId(agentId: string): Observable<IOperatorIdResponse> {
        return this.#http.get<IOperatorIdResponse>(
            `${environment.voiceUrl}/data/operator/id/${agentId}`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Agent Service (Get operator identity)',
                ),
            },
        );
    }
}
